import axios from 'axios'
import { directoryApiClient } from './auth'

export type UserProfile = {
  uuid: string
  email: string
  phone_number?: string
  given_name?: string
  family_name?: string
  locale?: string
  avatar_key?: string
  avatar_url?: string
  national_identifier?: string
}

export type UpdateUserProfileParams = {
  email?: string
  given_name?: string
  family_name?: string
  locale?: string
  avatar?: {
    key: string
  }
}

export type GetUserProfileOptions = {
  signal?: AbortSignal
  /** See `skipAuthRefresh` in `tokenRefresh.ts` — for best-effort, non-critical callers. */
  skipAuthRefresh?: boolean
}

export const getUserProfile = async (
  accessToken: string,
  options?: GetUserProfileOptions,
): Promise<UserProfile> => {
  const { data } = await directoryApiClient.get<{ user: UserProfile }>('/api/users/me', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    signal: options?.signal,
    skipAuthRefresh: options?.skipAuthRefresh,
  })
  return data.user
}

export const updateUserProfile = async (
  accessToken: string,
  params: UpdateUserProfileParams,
  twoStepVerificationCode?: string,
): Promise<void> => {
  await directoryApiClient.put(
    '/api/users/me',
    { user: params },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(twoStepVerificationCode && { 'X-Swile-Two-Step-Verification-Code': twoStepVerificationCode }),
      },
    },
  )
}

export type AvatarConstraints = {
  allowed_content_types: string[]
  max_size_bytes: number
}

export const getAvatarConstraints = async (accessToken: string): Promise<AvatarConstraints> => {
  const { data } = await directoryApiClient.get<AvatarConstraints>('/api/users/avatar/constraints', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
  return data
}

export const uploadAvatar = async (accessToken: string, file: File): Promise<{ key: string; url: string }> => {
  const { data } = await directoryApiClient.post<{ upload: { key: string; url: string } }>(
    '/api/users/avatar/upload_url',
    {
      upload: {
        filename: file.name,
        content_type: file.type,
        content_length: file.size,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  return data.upload
}

export class InvalidOldPasswordError extends Error {
  constructor() {
    super('Invalid old password')
    this.name = 'InvalidOldPasswordError'
  }
}

export class PasswordAlreadyUsedError extends Error {
  constructor() {
    super('Password already used')
    this.name = 'PasswordAlreadyUsedError'
  }
}

export const changePassword = async (accessToken: string, oldPassword: string, newPassword: string): Promise<void> => {
  try {
    await directoryApiClient.post(
      '/oauth/accounts/update_password',
      { old_password: oldPassword, password: newPassword },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new InvalidOldPasswordError()
      }
      if (error.response?.status === 422) {
        throw new PasswordAlreadyUsedError()
      }
    }

    throw error
  }
}

export class AvatarUploadValidationError extends Error {
  constructor(
    message: string,
    public readonly code: 'AVATAR_INVALID_TYPE' | 'AVATAR_FILE_TOO_LARGE',
  ) {
    super(message)
    this.name = 'AvatarUploadValidationError'
  }
}

export const validateAvatarFile = (file: File, constraints: AvatarConstraints): void => {
  const allowedTypes = constraints.allowed_content_types
  if (!(file.type && allowedTypes.includes(file.type))) {
    throw new AvatarUploadValidationError('Invalid file type', 'AVATAR_INVALID_TYPE')
  }
  if (file.size > constraints.max_size_bytes) {
    throw new AvatarUploadValidationError('File too large', 'AVATAR_FILE_TOO_LARGE')
  }
}

export const uploadAvatarToS3 = async (url: string, file: File, constraints: AvatarConstraints): Promise<void> => {
  validateAvatarFile(file, constraints)

  const response = await fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
      'x-amz-acl': 'public-read',
    },
  })

  if (!response.ok) {
    throw new Error(`Avatar upload failed: ${response.status}`)
  }
}
